<template>
  <div class="StockStatusFutureAvailability">
    <p class="hidden">StockStatusFutureAvailability</p>
    <LoadingIcon v-if="futureDeliveriesLoading" />
    <div v-else-if="futureDeliveriesDisplay.display">
      <div class="flex flex-wrap items-center gap-12">
        <div
            v-for="(fdRow, index) in futureDeliveriesDisplay.rows"
            :key="index"
            class="flex items-center gap-6"
        >
          <div class="inline-flex items-center justify-center w-[14px] h-[14px]">
            <div
                class="w-8 h-8 rounded-full border relative top-1 bg-bg-successStrong border-border-successStrong"
                :class="{
              'bg-bg-successStrong border-border-successStrong': fdRow.color === 'green',
              'bg-bg-warning border-border-warningStrong': fdRow.color === 'yellow',
              'bg-bg-warningStrong border-border-warningStrong': fdRow.color === 'red',
              }"
            />
          </div>
          <div>{{ fdRow.text }}</div>
        </div>
      </div>    </div>
  </div>
</template>

<script setup lang="ts">
import NP from 'number-precision';
import { ProductVariantModel } from '~/models/productVariant';
import usePackageAreaConverter from '~/composeables/usePackageAreaConverter';
import {
  type FutureDeliveriesDisplay,
  checkForFutureDelivery
} from '~/composeables/useStockStatus';
import { useCartStore } from '~/store/cart';
import { CartApiItem } from '~/constants/types/norce';
import { NorceCartItemStatusId } from '~/constants/norceCodes';
import useWarehouse from '~/composeables/useWarehouse';

const cartStore = useCartStore();
const { getVariantStockValue } = useWarehouse();

NP.enableBoundaryChecking(false);

/*
interface Props {
  activeVariant: ProductVariantModel,
  totalQuantity?: number
}

const props = withDefaults(defineProps<Props>(), {
  totalQuantity: 1,
});
*/

const props = defineProps<{
  activeVariant?: ProductVariantModel,
  cartItem?: CartApiItem
  totalQuantity: number,
}>();

const emit = defineEmits<{
  (e: 'setBuyButtonDisabled', payload: boolean): void;
  (e: 'loading', payload: boolean): void;
}>();

let stockvalue = 0;
if (props.activeVariant) {
  stockvalue = getVariantStockValue(props.activeVariant);
} else if (props.cartItem?.onHandValue) {
  stockvalue = props.cartItem.onHandValue;
}

let partNo = '';
let isExpiring = false;
let source = 'pdp' as 'pdp' | 'cart';

if (props.activeVariant) {
  partNo = props.activeVariant.partNo;
  isExpiring = props.activeVariant.isExpiring;
} else if (props.cartItem) {
  partNo = props.cartItem.partNo;
  isExpiring = props.cartItem.statusId === NorceCartItemStatusId.Expiring;
  source = 'cart';
}

const {
  unitDisplay,
  convertPackageToArea,
  shouldUseArea,
} = usePackageAreaConverter(toRef(()=>props.activeVariant ? props.activeVariant : props.cartItem));

const futureDeliveriesLoading = ref(true);

const futureDeliveriesDisplay = ref<FutureDeliveriesDisplay>({ display: false, lockBuyButton: false });

onMounted(()=> {
  setTimeout(setFutureDeliveriesDisplay, 20);
});

const setFutureDeliveriesDisplay = async() => {
  const onHandArea = convertPackageToArea(stockvalue) || 0;
  const requestedArea = convertPackageToArea(props.totalQuantity) || 0;
  const alreadyInCart = cartStore.getQuantityFromPartNo(partNo);
  const inCartArea = convertPackageToArea(alreadyInCart) || 0;

  // if we are already in cart, we only get status for whats in the cart already
  const totalAreaNeeded = source === 'cart'? inCartArea : NP.plus(requestedArea, inCartArea);

  futureDeliveriesLoading.value = true;
  emit('loading', true);
  futureDeliveriesDisplay.value = await checkForFutureDelivery(
    partNo,
    totalAreaNeeded,
    onHandArea,
    shouldUseArea.value,
    unitDisplay,
    isExpiring
  );
  futureDeliveriesLoading.value = false;
  emit('loading', false);
  emit('setBuyButtonDisabled', futureDeliveriesDisplay.value.lockBuyButton);
};

watch(
  ()=>props.totalQuantity,
  () => { setFutureDeliveriesDisplay();
  }
);

watch(
  ()=> cartStore.getQuantityFromPartNo(partNo),
  () => {
    setFutureDeliveriesDisplay();
  }
);

</script>

<style scoped lang="postcss">
</style>
